/**
 * 系统路由配置
 * {
 *   name: '',          // 路由名称，系统导航面包屑会用到
 *   path: '',          // 路由地址
 *   component: '',     // 组件path，相对于src/pages的路径
 *   redirect: '',      // 重定向路径
 *   authorized: true,  // 是否需要鉴权认证，目前只允许设置为true|false，后面可考虑自定义Authorized类
 *   routes: [{...}],   // 子路由配置
 * }
 *
 * 1. 组件以src/pages为相对路径，文件后缀为.js的文件，目录中的，默认为index.js;
 * 2. 按约定，一级路由为Layout组件，其子路由为业务组件（页面），
 *    业务组件路由参数exact默认为true，可手动设为false，但要注意路由的顺序;
 * 3. 业务组件（页面）路由命名按以下约定格式定义，可以展开、高亮选中其关联的菜单项，如：
 *    a. /users（用户列表）
 *    b. /users/detail/:userID（用户详情）
 *    如果其内置页（用户详情）路由地址包含了用户列表路由地址（/users），
 *    那么在路由到该页面时，会自动展开并高亮选中对应的菜单项（用户列表菜单项）。
 */

export default [
    // 外部无须授权页面布局，带顶部、页尾
    {
        path: '/user',
        component: import(/* webpackChunkName: "page-index" */ '@/pages/layout/user-layout'),
        routes: [{
            path: '/user',
            redirect: '/user/login',
        }, {
            name: '登录',
            path: '/user/login',
            component: import(/* webpackChunkName: "page-login" */ "@/pages/user/login")
        }, {
            name: '注册',
            path: '/user/register',
            component: import(/* webpackChunkName: "page-register" */ "@/pages/user/register")
        },

        // 独立功能页
        {
            name: '服务开通流程',
            path: '/user/process',
            component: import(/* webpackChunkName: "page-process" */ "@/pages/user/process")
        }, {
            name: '忘记密码',
            path: '/user/forget-password',
            component: import(/* webpackChunkName: "page-forget-password" */ "@/pages/user/forget-password/qrcode-page")
        }, {
            name: '联系我们',
            path: '/user/contact-us',
            component: import(/* webpackChunkName: "page-contact-us" */ "@/pages/user/contact-us")
        }, {
            name: '升级日志',
            path: '/user/update-logs',
            component: import(/* webpackChunkName: "page-update-logs" */ "@/pages/user/sys-update-logs")
        },{
            name: '关于产品',
            path: '/user/about-product',
            component: import(/* webpackChunkName: "page-about-product" */ "@/pages/user/about-product")
        }, {
            name: '安装包下载',
            path: '/user/pack-download',
            component: import(/* webpackChunkName: "page-code-help" */ '@/pages/study-center/pack-download')
        }, {
            name: '操作手册下载',
            path: '/user/help-download',
            component: import(/* webpackChunkName: "page-code-help" */ '@/pages/study-center/help-download')
        }]
    },

    // 需要授权的外部user布局页面，带顶部、页尾
    {
        path: '/user-auth',
        component: import(/* webpackChunkName: "page-index" */ '@/pages/layout/user-layout'),
        authorized: true,
        routes: [
            // 合同签署
            {
                name: '签署结果',
                path: '/user-auth/contract-result',
                component: import(/* webpackChunkName: "page-code-help" */ '@/pages/user/contract/result')
            }, {
                name: '合同过期',
                path: '/user-auth/contract-overdue',
                component: import(/* webpackChunkName: "page-code-help" */ '@/pages/user/contract/overdue')
            },
        ]
    },

    // 外部页面布局，独立于应用的页面
    {
        path: '/external',
        component: import(/* webpackChunkName: "page-layout" */ '@/pages/layout/external-layout'),
        routes: [{
            name: '打印二维码小票',
            path: '/external/print-ticket',
            component: import(/* webpackChunkName: "page-print-ticket" */ '@/pages/invoice-center/small-ticket/sm-ticket')
        }]
    },

    // 需要授权的外部页面布局
    {
        path: '/external-auth',
        component: import(/* webpackChunkName: "page-layout" */ '@/pages/layout/external-layout'),
        authorized: true,
        routes: [
            // 合同签署
            {
                name: '签署合同',
                path: '/external-auth/sign-contract',
                component: import(/* webpackChunkName: "page-code-help" */ '@/pages/user/contract/sign-contract')
            },
            {
                name: '签署合同',
                path: '/external-auth/sign-contract-h5',
                component: import(/* webpackChunkName: "page-code-help" */ '@/pages/user/contract/sign-contract-h5')
            }
        ]
    },

    // 应用布局，带顶部操作栏、左菜单
    {
        path: '/',
        component: import(/* webpackChunkName: "page-layout" */ '@/pages/layout/app-layout'),
        authorized: true,
        routes: [{
            path: '/',
            redirect: '/home',
        },
        // 首页模块
        {
            name: '首页',
            path: '/home',
            redirect: '/home/index',
            routes: [{
                name: '首页',
                path: '/home/index',
                component: import(/* webpackChunkName: "page-home" */ '@/pages/home/index')
            }]
        },
        // 发票中心
        {
            name: '发票中心',
            path: '/invoice-center',
            redirect: '/invoice-center/audit',
            routes: [{
                name: '小程序开票审核',
                path: '/invoice-center/audit',
                component: import(/* webpackChunkName: "page-audit" */ '@/pages/invoice-center/audit')
            }, {
                name: '二维码小票开票',
                path: '/invoice-center/menu-invoice',
                redirect: '/invoice-center/menu-invoice/audit',
                routes: [{
                    name: '创建二维码小票',
                    path: '/invoice-center/menu-invoice/add-invoice',
                    component: import(/* webpackChunkName: "page-add-invoice" */ '@/pages/invoice-center/add-invoice')
                }, {
                    name: '二维码小票记录',
                    path: '/invoice-center/menu-invoice/small-ticket',
                    component: import(/* webpackChunkName: "page-small-ticket" */ '@/pages/invoice-center/small-ticket')
                }]
            }, {
                name: '纸票管理',
                path: '/invoice-center/paper-invoice-manage',
                component: import(/* webpackChunkName: "page-paper-invoice" */ '@/pages/invoice-center/paper-invoice-manage')
            }, {
                name: '批量开票',
                path: '/invoice-center/batch-invoice',
                component: import(/* webpackChunkName: "page-batch-invoice" */ '@/pages/invoice-center/batch-invoice')
            }, {
                name: '开票数据',
                path: '/invoice-center/menu-record',
                redirect: '/invoice-center/menu-record/records',
                routes: [{
                    name: '开票明细记录',
                    path: '/invoice-center/menu-record/records',
                    component: import(/* webpackChunkName: "page-records" */ '@/pages/invoice-center/records')
                }, {
                    name: '发票月度汇总',
                    path: '/invoice-center/menu-record/total',
                    component: import(/* webpackChunkName: "page-total" */ '@/pages/invoice-center/total')
                }, {
                    name: '分税率统计',
                    path: '/invoice-center/menu-record/tax-count',
                    component: import(/* webpackChunkName: "page-study-center" */ '@/pages/invoice-center/tax-count')
                }]
            }, {
                name: '发票查验',
                path: '/invoice-center/invoice-inspec',
                redirect: '/invoice-center/menu-check',
                routes: [{
                    name: '单张查验',
                    path: '/invoice-center/menu-check/invoice-inspec',
                    component: import(/* webpackChunkName: "page-invoice-inspec" */ '@/pages/invoice-center/invoice-inspec')
                }]
            }, {
                name: '在线人工开票',
                path: '/invoice-center/manual-invoice',
                component: import(/* webpackChunkName: "page-manual-invoice" */ '@/pages/invoice-center/manual-invoice')
            }, {
                name: '万达智慧酒店开票请求',
                path: '/invoice-center/invoice-apply',
                component: import(/* webpackChunkName: "page-tax-disc" */ '@/pages/invoice-center/invoice-apply')
            }, {
                name: '通用智慧酒店开票请求',
                path: '/invoice-center/invoice-apply-common',
                component: import(/* webpackChunkName: "page-tax-disc" */ '@/pages/invoice-center/invoice-apply-common')
            }]
        },

        // 收款中心
        {
            name: '收款中心',
            path: '/receipt-center',
            redirect: '/receipt-center/register',
            routes: [{
                name: '开通收款',
                path: '/receipt-center/register',
                component: import(/* webpackChunkName: "page-collection" */ '@/pages/receipt-center/register')
            }, {
                name: '收款记录',
                path: '/receipt-center/records',
                component: import(/* webpackChunkName: "page-collection" */ '@/pages/receipt-center/records')
            }, {
                name: '账单下载',
                path: '/receipt-center/bill',
                component: import(/* webpackChunkName: "page-bill" */ '@/pages/receipt-center/bill')
            }, {
                name: '收款管理',
                path: '/receipt-center/pay-invoice-manage',
                component: import(/* webpackChunkName: "page-bill" */ '@/pages/receipt-center/pay-invoice-manage')
            }, ]
        },

        // 学习/下载中心
        {
            name: '学习/下载中心',
            path: '/study-center',
            redirect: '/study-center/teach',
            routes: [{
                name: '教学视频',
                path: '/study-center/teach',
                component: import(/* webpackChunkName: "page-code-manage" */ '@/pages/study-center/teach')
            }, {
                name: '安装包下载',
                path: '/study-center/pack-download',
                component: import(/* webpackChunkName: "page-code-help" */ '@/pages/study-center/pack-download')
            }, {
                name: '操作手册下载',
                path: '/study-center/help-download',
                component: import(/* webpackChunkName: "page-code-help" */ '@/pages/study-center/help-download')
            }]
        },

        // 增值服务
        {
            name: '增值服务',
            path: '/vip-service',
            redirect: '/vip-service/version-upgrade',
            routes: [{
                name: '版本升级',
                path: '/vip-service/version-upgrade',
                component: import(/* webpackChunkName: "page-version-upgrade" */ "@/pages/vip-service/version-upgrade")
            }, {
                name: '我的订单',
                path: '/vip-service/order',
                component: import(/* webpackChunkName: "page-order" */ '@/pages/vip-service/order')
            }]
        },

        // 管理中心
        {
            name: '管理中心',
            path: '/manage-center',
            redirect: '/manage-center/shop-manage',
            routes: [{
                name: '门店管理',
                path: '/manage-center/shop-manage',
                component: import(/* webpackChunkName: "page-shop-manage" */ '@/pages/manage-center/shop-manage'),
                routes: [{
                    name: '门店详情',
                    path: '/manage-center/shop-manage/detail',
                    component: import(/* webpackChunkName: "page-shop-detail" */ '@/pages/manage-center/shop-manage/detail')
                }]
            }, {
                name: '开票项管理',
                path: '/manage-center/menu-code',
                redirect: '/manage-center/menu-code/code-manage',
                routes: [{
                    name: '商品编码管理',
                    path: '/manage-center/menu-code/code-manage',
                    component: import(/* webpackChunkName: "page-code-manage" */ '@/pages/manage-center/code-manage'),
                    routes: [{
                        path: '/manage-center/menu-code/code-manage/add',
                        component: import(/* webpackChunkName: "page-add-form" */ '@/pages/manage-center/code-manage/add-form')
                    }]
                }, {
                    name: '商品编码批量导入',
                    path: '/manage-center/menu-code/code-import',
                    component: import(/* webpackChunkName: "page-study-center" */ '@/pages/manage-center/code-import')
                }]
            }, {
                name: '税盘管理',
                path: '/manage-center/tax-disc',
                component: import(/* webpackChunkName: "page-tax-disc" */ '@/pages/manage-center/tax-disc')
            }, {
                name: '账号管理',
                path: '/manage-center/account-manage',
                component: import(/* webpackChunkName: "page-account-manage" */ '@/pages/manage-center/account-manage')
            }, {
                name: '发票盒子管理',
                path: '/fpbox-manage',
                component: import(/* webpackChunkName: "page-fpbox-manage" */ '@/pages/manage-center/fpbox-manage')
            }, {
                name: '系统设置',
                path: '/manage-center/sys-setup',
                component: import(/* webpackChunkName: "page-sys-setup" */ '@/pages/manage-center/sys-setup')
            }, {
                name: '合同管理',
                path: '/manage-center/contract-manage',
                component: import(/* webpackChunkName: "page-sys-setup" */ '@/pages/manage-center/contract-manage')
            }, {
                name: '支付开票设置',
                path: '/manage-center/payment-billing-settings',
                component: import(/* webpackChunkName: "page-payment-billing-settings" */ '@/pages/manage-center/payment-billing-settings')
            }]
        }]
    },

]
